import { ActionIcon, Avatar, Button, Divider, Flex, Modal, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { IconPhoto, IconSearch, IconX } from "@tabler/icons-react";
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { userApi } from "../../../apis";
import { PageFilter, PageLayout } from "../../../components/layout";
import { ColumnType, ITableRef, Table } from "../../../components/table";
import { Tag } from "../../../components/tags";
import RenderText from "../../../components/ui/render-text";
import { PartnerManagementUserForm } from "../../../components/user/form";
import { IAllUser } from "../../../interfaces/IAllUser";
import { IGeneral } from "../../../interfaces/IGeneral";
import { IReference } from "../../../models/General";
import { dateSecFormat } from "../../../utils/date";
import { renderName } from "../../../utils/render-name";

function PartnerManagementUser(): React.JSX.Element {
  const ref = useRef<ITableRef>(null);
  const breadcrumb = useBreadcrumb();
  const [action, setAction] = React.useState<string[]>([]);
  const { userRegisterStatus } = useSelector((state: { general: IGeneral }) => state.general);
  const [query, setQuery] = React.useState("");
  const [queryDebounced] = useDebouncedValue(query, 400);

  const columns = useHeader({
    userRegisterStatus,
    onClick: (key, record) => {
      switch (key) {
        case "business": {
          setAction(["BUSINESS", record as any]);
          break;
        }
      }
    },
  });

  const onCancel = () => {
    setAction([]);
  };

  return (
    <>
      <PageLayout title="Хэрэглэгчийн жагсаалт" subTitle="DE-HUB бизнес платформд бүртгэлтэй нийт хэрэглэгчдийн жагсаалт" breadcrumb={breadcrumb}>
        <Divider mb={5} />

        <PageFilter
          left={[
            <TextInput
              key={1}
              value={query}
              w={400}
              onChange={(e) => setQuery(e.currentTarget.value)}
              placeholder="Хайлт хийх"
              leftSection={<IconSearch size={18} />}
              rightSection={
                query && (
                  <ActionIcon
                    onClick={() => {
                      setQuery("");
                    }}>
                    <IconX size={14} />
                  </ActionIcon>
                )
              }
            />,
          ]}
        />
        <Table ref={ref} name="partner-management-user.list" filters={{ query: queryDebounced }} columns={columns} loadData={(data) => userApi.list(data!)} />
      </PageLayout>
      <Modal opened={action[0] === "BUSINESS"} onClose={onCancel} withCloseButton={false} centered size="70%">
        <PartnerManagementUserForm action={action} onCancel={onCancel} />
      </Modal>
    </>
  );
}

export { PartnerManagementUser };

const useHeader = ({
  userRegisterStatus,
  onClick,
}: {
  userRegisterStatus: IReference[];
  onClick: (key: string, record: IAllUser) => void;
}): ColumnType<IAllUser>[] => [
  {
    title: "#",
    width: "30px",
    render: (_r, index) => <RenderText text={index + 1} />,
  },
  {
    title: "Зураг",
    sorter: true,
    dataIndex: "avatar",
    render: (record) => {
      return (
        <Avatar src={record?.avatar} alt="">
          <IconPhoto />
        </Avatar>
      );
    },
  },
  {
    title: "Username",
    sorter: true,
    dataIndex: "username",
    render: (record) => <RenderText text={record?.username} />,
  },
  {
    title: "Овог",
    sorter: true,
    dataIndex: "lastName",
    render: (record) => <RenderText text={record?.lastName ?? "-"} />,
  },
  {
    title: "Нэр",
    sorter: true,
    dataIndex: "firstName",
    render: (record) => <RenderText text={record?.firstName ?? "-"} />,
  },
  {
    title: "Имэйл",
    sorter: true,
    dataIndex: "email",
    render: (record) => <RenderText text={record?.email ?? "-"} />,
  },
  {
    title: "Утас",
    sorter: true,
    dataIndex: "phone",
    render: (record) => <RenderText text={record?.phone ?? "-"} />,
  },
  {
    title: "Бизнесийг харах",
    sorter: true,
    dataIndex: "businessCount",
    render: (record) => <RenderText text={record?.businessCount ?? 0} />,
  },
  {
    title: "Бизнесийг харах",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => {
      return (
        <>
          {record?.businessCount > 0 ? (
            <Button variant="default" radius="sm" onClick={() => onClick("business", record)}>
              Дэлгэрэнгүй
            </Button>
          ) : (
            "-"
          )}
        </>
      );
    },
  },
  {
    title: "Бүртгэсэн ажилтан",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => {
      return (
        <Flex align="center" gap="sm">
          <Avatar src={record?.regUser?.avatar}>
            <IconPhoto />
          </Avatar>
          <RenderText text={renderName(record?.regUser)} />
        </Flex>
      );
    },
  },
  {
    title: "Бүртгэсэн огноо",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => <RenderText text={dateSecFormat(record?.createdAt)} />,
  },
  {
    title: "Бүртгэлийн статус",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => (
      <Tag
        color={(userRegisterStatus ?? []).find((item: IReference) => item.code === record?.registerStatus)?.color || "gray.5"}
        name={(userRegisterStatus ?? []).find((item: IReference) => item.code === record?.registerStatus)?.name ?? "Амжилтгүй"}
      />
    ),
  },
  {
    title: "Веб хэрэглэгч эсэх",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => <Tag name={record.isSysUser ? "Тийм" : "Үгүй"} color={record.isSysUser ? "green.5" : "red.5"} />,
  },
  {
    title: "Апп хэрэглэгч эсэх",
    sorter: true,
    dataIndex: "familyName",
    render: (record) => <Tag name={record.isAppUser ? "Тийм" : "Үгүй"} color={record.isAppUser ? "green.5" : "red.5"} />,
  },
];

const useBreadcrumb = () => [
  {
    to: "/",
    label: "Эхлэл",
  },
  {
    label: "Хэрэглэгчийн жагсаалт",
  },
];
